import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, Banknote } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useContent } from "@/lib/content";
import { formatPrice } from "@/lib/format";

export default function CartDrawer() {
  const { t } = useContent();
  const { items, open, setOpen, setQty, remove, subtotal, clear } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    setOpen(false);
    navigate("/commander");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            className="fixed inset-0 z-[80] bg-teal-deep/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 z-[90] h-full w-full max-w-md bg-white flex flex-col shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-line">
              <h2 className="font-serif text-2xl text-teal">{t("cart.title")}</h2>
              <button data-testid="cart-drawer-close" onClick={() => setOpen(false)} aria-label="Fermer le panier" className="text-ink hover:text-teal">
                <X size={24} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 gap-4 px-8 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cream">
                  <ShoppingBag size={26} className="text-teal" />
                </span>
                <p className="text-ink/70">{t("cart.empty")}</p>
                <button
                  data-testid="cart-drawer-empty-cta"
                  onClick={() => { setOpen(false); navigate("/boutique"); }}
                  className="rounded-full bg-teal text-cream px-6 py-2.5 text-sm font-medium hover:bg-teal-dark transition-colors"
                >
                  Découvrir la boutique
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                  {items.map((item) => (
                    <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-4 py-3 border-b border-line/70">
                      <img src={item.image} alt={item.name} className="h-20 w-16 rounded-xl object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-lg text-teal-dark leading-tight truncate">{item.name}</p>
                        <p className="text-sm text-mauve mt-0.5">{item.variant && <span data-testid={`cart-variant-${item.key}`} className="mr-2 rounded-full bg-mint/30 px-2 py-0.5 text-[11px] text-teal-dark">{item.variant}</span>}{formatPrice(item.price)}</p>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex items-center rounded-full border border-line">
                            <button data-testid={`cart-qty-minus-${item.key}`} onClick={() => setQty(item.key, item.qty - 1)} className="p-1.5 text-ink hover:text-teal" aria-label="Diminuer la quantité"><Minus size={14} /></button>
                            <span data-testid={`cart-qty-${item.key}`} className="w-8 text-center text-sm font-medium">{item.qty}</span>
                            <button data-testid={`cart-qty-plus-${item.key}`} onClick={() => setQty(item.key, item.qty + 1)} disabled={item.qty >= item.stock} className="p-1.5 text-ink hover:text-teal disabled:opacity-30" aria-label="Augmenter la quantité"><Plus size={14} /></button>
                          </div>
                          <button data-testid={`cart-remove-${item.key}`} onClick={() => remove(item.key)} className="text-xs text-mauve underline hover:text-[#b04a4a]">Retirer</button>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-teal whitespace-nowrap">{formatPrice(item.price * item.qty)}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-line px-6 py-5 space-y-3">
                  <div className="flex justify-between text-sm text-ink/80">
                    <span>Sous-total</span>
                    <span data-testid="cart-subtotal" className="font-semibold text-teal">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-ink/60">
                    <Banknote size={15} className="text-teal shrink-0 mt-0.5" />
                    <span>{t("cart.notice")}</span>
                  </div>
                  <button
                    data-testid="cart-drawer-checkout"
                    onClick={goToCheckout}
                    className="w-full rounded-full bg-teal text-cream py-3.5 font-medium hover:bg-teal-dark transition-colors active:scale-[0.99]"
                  >
                    {t("cart.checkout")}
                  </button>
                  <button data-testid="cart-drawer-clear" onClick={clear} className="w-full text-xs text-mauve underline">Vider le panier</button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
